import { ReflectUtils } from "@dooboostore/core";
import { ensureInit } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
const EMIT_CUSTOM_EVENT_METADATA_KEY = Symbol.for("simple-web-component:emit-custom-event");
function emitCustomEvent(selectorOrTarget, typeOrPropertyKey, optionsOrDescriptor) {
  if (typeof selectorOrTarget === "object" && typeof typeOrPropertyKey === "string" && typeof optionsOrDescriptor === "object" && "value" in optionsOrDescriptor) {
    throw new Error(`@emitCustomEvent decorator cannot be used on properties. (Property: ${String(typeOrPropertyKey)})`);
  }
  if ((typeof selectorOrTarget === "string" || typeof selectorOrTarget === "function") && typeof typeOrPropertyKey === "string") {
    return createEmitDecorator(selectorOrTarget, typeOrPropertyKey, optionsOrDescriptor);
  }
  if (typeof selectorOrTarget === "string" && typeOrPropertyKey !== "string") {
    return createEmitDecorator("$this", selectorOrTarget, typeOrPropertyKey);
  }
  throw new Error("Invalid @emitCustomEvent usage");
}
function createEmitDecorator(selector, type, options) {
  return (targetObj, propertyKey, descriptor) => {
    const constructor = targetObj.constructor;
    const fullOptions = { ...options || {}, type };
    if (fullOptions.bubbles === void 0)
      fullOptions.bubbles = true;
    if (fullOptions.composed === void 0)
      fullOptions.composed = true;
    let list = ReflectUtils.getOwnMetadata(EMIT_CUSTOM_EVENT_METADATA_KEY, constructor);
    if (!list) {
      list = [];
      ReflectUtils.defineMetadata(EMIT_CUSTOM_EVENT_METADATA_KEY, list, constructor);
    }
    list.push({ propertyKey, selector, type, options: fullOptions });
    const original = descriptor.value;
    descriptor.value = function(...args) {
      ensureInit(this);
      const res = original.apply(this, args);
      const extractValue = (v) => {
        const keyToUse = fullOptions.valueKey ?? EMIT_CUSTOM_EVENT_METADATA_KEY;
        if (v && typeof v === "object" && keyToUse in v)
          return v[keyToUse];
        return v;
      };
      const handleResult = (result) => {
        const detail = extractValue(result);
        const event = new CustomEvent(type, {
          detail,
          bubbles: fullOptions.bubbles,
          composed: fullOptions.composed,
          cancelable: fullOptions.cancelable
        });
        const currentWin = SwcUtils.resolveWindow(this);
        const hostSet = SwcUtils.getHelperAndHostSet(this);
        const eventTargets = [];
        let resolvedSelector = selector;
        if (typeof selector === "function") {
          resolvedSelector = selector(this, hostSet);
        }
        if (resolvedSelector instanceof currentWin.Element) {
          eventTargets.push(resolvedSelector);
        } else if (resolvedSelector instanceof currentWin.NodeList) {
          eventTargets.push(...Array.from(resolvedSelector));
        } else if (Array.isArray(resolvedSelector)) {
          eventTargets.push(...resolvedSelector);
        } else if (resolvedSelector === null) {
        } else {
          const stringSelector = resolvedSelector;
          if (stringSelector === "$window")
            eventTargets.push(currentWin);
          else if (stringSelector === "$document")
            eventTargets.push(currentWin.document);
          else if (stringSelector === "$host") {
            if (hostSet.$host)
              eventTargets.push(hostSet.$host);
          } else if (stringSelector === "$parentHost") {
            if (hostSet.$parentHost)
              eventTargets.push(hostSet.$parentHost);
          } else if (stringSelector === "$appHost") {
            if (hostSet.$appHost)
              eventTargets.push(hostSet.$appHost);
          } else if (stringSelector === "$firstHost") {
            if (hostSet.$firstHost)
              eventTargets.push(hostSet.$firstHost);
          } else if (stringSelector === "$lastHost") {
            if (hostSet.$lastHost)
              eventTargets.push(hostSet.$lastHost);
          } else if (stringSelector === "$firstAppHost") {
            if (hostSet.$firstAppHost)
              eventTargets.push(hostSet.$firstAppHost);
          } else if (stringSelector === "$lastAppHost") {
            if (hostSet.$lastAppHost)
              eventTargets.push(hostSet.$lastAppHost);
          } else if (stringSelector === "$hosts") {
            hostSet.$hosts.forEach((h) => eventTargets.push(h));
          } else if (stringSelector === "$appHosts") {
            hostSet.$appHosts.forEach((h) => eventTargets.push(h));
          } else if (stringSelector === "$this" || !stringSelector) {
            eventTargets.push(this);
          } else {
            const r = fullOptions.root || "auto";
            const searchRoots = [];
            if (r === "auto")
              searchRoots.push(this.shadowRoot || this);
            else if (r === "light")
              searchRoots.push(this);
            else if (r === "shadow" && this.shadowRoot)
              searchRoots.push(this.shadowRoot);
            else if (r === "all") {
              searchRoots.push(this);
              if (this.shadowRoot)
                searchRoots.push(this.shadowRoot);
            }
            searchRoots.forEach((sr) => {
              sr.querySelectorAll(stringSelector).forEach((el) => eventTargets.push(el));
            });
          }
        }
        eventTargets.forEach((t) => {
          if (fullOptions.filter && !fullOptions.filter(t, detail, { currentThis: this, helper: hostSet })) {
            return;
          }
          t.dispatchEvent(event);
        });
        return result;
      };
      if (res instanceof Promise) {
        return res.then(handleResult);
      } else {
        return handleResult(res);
      }
    };
  };
}
const getEmitCustomEventMetadataList = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(EMIT_CUSTOM_EVENT_METADATA_KEY, constructor).flat();
};
const emit = emitCustomEvent;
function emitAppHost(type, options) {
  return emitCustomEvent("$appHost", type, options);
}
function emitWindow(type, options) {
  return emitCustomEvent("$window", type, options);
}
function emitDocument(type, options) {
  return emitCustomEvent("$document", type, options);
}
function emitLight(selector, type, options) {
  return emitCustomEvent(selector, type, { ...options ?? {}, root: "light" });
}
function emitShadow(selector, type, options) {
  return emitCustomEvent(selector, type, { ...options ?? {}, root: "shadow" });
}
function emitAll(selector, type, options) {
  return emitCustomEvent(selector, type, { ...options ?? {}, root: "all" });
}
class EmitCustomEventLifeCycler {
  constructor() {
    this.hostEmitMeta = null;
  }
  getHostEmitMeta(inst) {
    if (!this.hostEmitMeta) {
      const all = getEmitCustomEventMetadataList(inst) ?? [];
      this.hostEmitMeta = all.filter((m) => m.selector === "$this" || m.selector === "");
    }
    return this.hostEmitMeta;
  }
  /** observedAttributes 에 포함할 attributeName 목록 */
  getObservedAttributeNames(inst) {
    return this.getHostEmitMeta(inst).map((m) => m.options.attributeName).filter(Boolean);
  }
  onConnected(_helperHostSet) {
  }
  /** attributeChangedCallback 에서 elementDefine 이 직접 호출 */
  onAttributeChanged(helperHostSet, name, _old, newVal) {
    const inst = helperHostSet.$this;
    const matched = this.getHostEmitMeta(inst).find((m) => m.options.attributeName === name);
    if (matched && newVal !== null) {
      inst._bindAttributeEvent?.(inst, name, newVal, matched.type);
    }
  }
}
export {
  EMIT_CUSTOM_EVENT_METADATA_KEY,
  EmitCustomEventLifeCycler,
  emit,
  emitAll,
  emitAppHost,
  emitCustomEvent,
  emitDocument,
  emitLight,
  emitShadow,
  emitWindow,
  getEmitCustomEventMetadataList
};
//# sourceMappingURL=emitCustomEvent.js.map
